// Shared API service with authentication
import axios, { AxiosInstance } from 'axios';

type RequestParams = Record<string, unknown>;

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api',
      timeout: 10000,
    });

    // Request interceptor to attach auth token
    this.api.interceptors.request.use(
      config => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => Promise.reject(error),
    );

    // Response interceptor for token refresh
    this.api.interceptors.response.use(
      response => response,
      async error => {
        if (error.response?.status === 401) {
          // Try to refresh token
          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const response = await axios.post(`${this.api.defaults.baseURL}/auth/token/refresh/`, {
                refresh: refreshToken,
              });
              const { access } = response.data;
              localStorage.setItem('token', access);

              // Retry original request
              error.config.headers.Authorization = `Bearer ${access}`;
              return this.api.request(error.config);
            }
          } catch (refreshError) {
            // Refresh failed, clear auth and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      },
    );
  }

  // Generic CRUD methods
  async get<T>(url: string): Promise<T> {
    const response = await this.api.get<T>(url);
    return response.data;
  }

  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.api.post<T>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.api.put<T>(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.api.patch<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.api.delete<T>(url);
    return response.data;
  }

  // File upload helper
  async uploadFile(url: string, file: File, onProgress?: (progress: number) => void): Promise<unknown> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  // Pagination helper
  async getPaginated<T>(
    url: string,
    params?: RequestParams,
  ): Promise<{
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
  }> {
    const response = await this.api.get(url, { params });
    return response.data;
  }

  // Content API functions
  async getContents(filters?: { waypoint_name?: string; version?: number; museum_code?: string }): Promise<unknown[]> {
    const params = new URLSearchParams();
    if (filters?.waypoint_name) params.append('waypoint_name', filters.waypoint_name);
    if (filters?.version) params.append('version', filters.version.toString());
    if (filters?.museum_code) params.append('museum_code', filters.museum_code);

    const url = `/content/contents/${params.toString() ? `?${params.toString()}` : ''}`;
    return this.get(url);
  }

  async getContent(waypointName: string, version: number = 1): Promise<unknown> {
    return this.get(`/content/contents/${waypointName}/?version=${version}`);
  }

  async createContent(data: unknown): Promise<unknown> {
    return this.post('/content/contents/', data);
  }

  async updateContent(waypointName: string, version: number, data: unknown): Promise<unknown> {
    return this.put(`/content/contents/${waypointName}/?version=${version}`, data);
  }

  async deleteContent(waypointName: string, version: number): Promise<unknown> {
    return this.delete(`/content/contents/${waypointName}/?version=${version}`);
  }

  async getContentVersions(waypointName: string): Promise<unknown[]> {
    return this.get(`/content/contents/${waypointName}/versions/`);
  }

  // File upload API functions
  async uploadContentFile(file: File, fileType: string, contentId?: number, screen?: 'primary' | 'secondary') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    if (contentId !== undefined) {
      formData.append('content_id', contentId.toString());
    }
    if (screen) {
      formData.append('screen', screen);
    }

    const response = await this.api.post('/content/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async listS3Files(fileType: string = 'image') {
    const response = await this.api.get(`/content/s3-files/?file_type=${fileType}`);
    return response.data;
  }

  async deleteS3File(fileKey: string) {
    const response = await this.api.delete('/content/s3-files/delete/', {
      data: { file_key: fileKey },
    });
    return response.data;
  }

  // Authentication methods
  async changeOwnPassword(data: { old_password: string; new_password: string; new_password_confirm: string }) {
    return this.post('/auth/change-password/', data);
  }

  async changeOtherUserPassword(userId: number, newPassword: string, newPasswordConfirm: string) {
    return this.post(`/auth/users/${userId}/change-password/`, {
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    });
  }
}

// Singleton instance
export const apiService = new ApiService();
export default apiService;
